import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import getConfig from 'next/config';
import { connect } from 'react-redux';
import get from 'lodash.get';
import classNames from 'classnames';

import { globalLoadComplete } from 'redux/modules/router';
import { setPageView, setVertical } from 'redux/modules/shared';
import {
  loadVideo as loadVideoAction,
  setCurrent as setCurrentAction,
} from 'redux/modules/video';
import { loadRelatedArticle as loadRelatedArticleAction } from 'redux/modules/video/relatedArticle';
import { loadRelatedVideo as loadRelatedVideoAction } from 'redux/modules/video/relatedVideo';
import { loadTopPlaylist } from 'redux/modules/video/topPlaylists';
import {
  loadAssociatedPlaylist as loadAssociatedPlaylistAction,
  loadVideosByIds as loadVideosByIdsAction,
} from 'redux/modules/video/associatedPlaylists';

import {
  AUTOPLAY_CANONICAL_VIDEOS_MUTED_ON_MOBILE,
  IGNORE_AUTOPLAY_TEST,
  MOBILE_PLAYLIST_NUMBER_OF_VIDEOS,
} from 'lib/brandFeatures.json';
import BTE from 'lib/BTE';
import buildUrl from 'lib/buildUrl';
import {
  relatedVideoArticle as relatedVideoArticlePropType,
  socialMediaProfiles as socialMediaProfilePropType,
  video as videoPropType,
  videoPlaylist as videoPlaylistPropType,
} from 'lib/CustomPropTypes';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import determineDomain from 'lib/determineDomain';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import isVerticalVideo from 'lib/isVerticalVideo';
import MobileDetection from 'lib/MobileDetection';
import { navbar, NAVBAR_THEME } from 'lib/navbar';
import parseIdFromUrl from 'lib/parseIdFromUrl';
import parseQueryString from 'lib/parseQuerystring';
import $t from 'lib/trackingService';
import { parseOnClient } from 'lib/urlUtils';
import { getAutoplayTestGroup, AUTOPLAY_TEST_GROUPS } from 'lib/videoUtils';

import AdsBundle from 'components/AdsBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import BestOfPlaylist from 'components/BestOfPlaylist';
import ErrorBoundary from 'components/ErrorBoundary';
import PlaylistDrawer, { LAYOUT } from 'components/PlaylistDrawer';
import Playlister from 'components/Playlister';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import { GlobalMetadata, VideoMetadata } from 'components/PageMetadata';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import Taboola from 'components/Taboola';
import VideoDetails from 'components/VideoDetails';
import VideoPlayer from 'components/VideoPlayer';
import VideoAutoplayCapabilitiesDetector, { NoDetect } from 'components/VideoPlayer/AutoplayCapabilitiesDetector';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';

import ErrorPage from '../_error';

import 'assets/stylesheets/main.scss';
import 'assets/stylesheets/toolkit.scss';
import styles from './video.module.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';


const pageView = 'video';

const {
  publicRuntimeConfig: {
    ENABLE_AUTOPLAY_TEST,
  },
  publicRuntimeConfig,
} = getConfig();

const brandsWithTaboolaFeed = ['news', 'today', 'msnbc', 'mach', 'better', 'think', 'noticias'];

const vertTopPlaylists = {
  today: 'mmlsnnd_55339009',
  news: 'mmlsnnd_bestofnbc',
  mach: 'mmlsnnd_mach',
  better: 'mmlsnnd_better',
  think: 'mmlsnnd_think',
  msnbc: 'mmlsnnd_3096433-nnd',
};

const navbarConfig = {
  theme: ({ content, vertical }) => {
    const isToday = vertical === 'today';
    if (content.current.error) {
      return isToday ? NAVBAR_THEME.TRANSPARENT : NAVBAR_THEME.VERTICAL;
    }
    return isToday ? NAVBAR_THEME.LIGHT : NAVBAR_THEME.DARK;
  },
};

const mapStateToProps = ({
  video,
  shared,
}) => ({
  associatedPlaylists: video.associatedPlaylists,
  isChromeless: shared.isChromeless,
  nowPlaying: video.current,
  relatedArticle: video.relatedArticle,
  relatedVideo: video.relatedVideo,
  socialMediaProfiles: shared.socialMediaProfiles,
  topPlaylists: video.topPlaylists,
});

const mapActionsToProps = (dispatch) => ({
  loadVideo: (video, vertical) => dispatch(loadVideoAction(video, vertical)),
  loadAssociatedPlaylist: (playlistID) => dispatch(loadAssociatedPlaylistAction(playlistID)),
  loadVideosByIds: (ids) => dispatch(loadVideosByIdsAction(ids)),
  loadRelatedArticle: (id) => dispatch(loadRelatedArticleAction(id)),
  loadRelatedVideo: (id) => dispatch(loadRelatedVideoAction(id)),
  setCurrent: (video) => dispatch(setCurrentAction(video)),
});

class VideoPage extends React.Component {
  static async getInitialProps(ctx) {
    const {
      req: {
        params: {
          id,
          playlistId,
        },
      },
      res: {
        locals: {
          domain,
          hostname,
          services,
          vertical,
        },
      },
      store,
      asPath,
    } = ctx;

    // Populate props with HFS component data
    const layout = {};
    if (services && services.layout && services.layout.getComponent) {
      layout.header = await services.layout.getComponent(vertical, 'header');
      layout.footer = await services.layout.getComponent(vertical, 'footer');
    }

    const promises = [
      store.dispatch(loadVideoAction(id, vertical)),
      store.dispatch(setPageView(pageView)),
      store.dispatch(setVertical(vertical)),
    ];
    // Load playlist for BestOfPlaylist component below player
    if (vertTopPlaylists && vertTopPlaylists[vertical]) {
      promises.push(
        store.dispatch(loadTopPlaylist(vertTopPlaylists[vertical])),
      );
    }
    // View playlist
    if (playlistId) {
      promises.push(
        store.dispatch(loadAssociatedPlaylistAction(playlistId)),
      );
    }

    await Promise.all(promises);

    const videoStateAfterLoad = store.getState().video;
    const statusCode = get(videoStateAfterLoad, 'current.error.status', 200);

    if (statusCode >= 400) {
      ctx.res.statusCode = statusCode;
    }

    await store.dispatch(globalLoadComplete({
      domain,
      host: hostname,
      navigating: false,
      path: asPath,
    }));

    return {
      layout,
      pageView,
      path: asPath,
      statusCode,
      vertical,
    };
  }

  static propTypes = {
    associatedPlaylists: PropTypes.arrayOf(videoPlaylistPropType),
    isChromeless: PropTypes.bool.isRequired,
    layout: servicesLayoutPropType.isRequired,
    loadAssociatedPlaylist: PropTypes.func,
    loadRelatedArticle: PropTypes.func,
    loadRelatedVideo: PropTypes.func,
    loadVideo: PropTypes.func.isRequired,
    loadVideosByIds: PropTypes.func,
    nowPlaying: videoPropType.isRequired,
    relatedArticle: relatedVideoArticlePropType.isRequired,
    relatedVideo: relatedVideoArticlePropType.isRequired,
    setCurrent: PropTypes.func.isRequired,
    socialMediaProfiles: socialMediaProfilePropType.isRequired,
    statusCode: PropTypes.number.isRequired,
    themesCssPath: PropTypes.string,
    topPlaylists: PropTypes.arrayOf(videoPlaylistPropType),
    vertical: PropTypes.string.isRequired,
  };

  static defaultProps = {
    associatedPlaylists: [],
    loadAssociatedPlaylist: Function.prototype,
    loadRelatedArticle: Function.prototype,
    loadRelatedVideo: Function.prototype,
    loadVideosByIds: Function.prototype,
    themesCssPath: '',
    topPlaylists: [],
  };

  constructor(props) {
    super(props);

    const PLATFORM = MobileDetection.any() ? 'MOBILE' : 'DESKTOP';
    const AUTOPLAY_TEST_ANY_PERCENTAGE = publicRuntimeConfig[`AUTOPLAY_TEST_${PLATFORM}_ANY_PERCENTAGE`];
    const AUTOPLAY_TEST_UNMUTED_PERCENTAGE = publicRuntimeConfig[`AUTOPLAY_TEST_${PLATFORM}_UNMUTED_PERCENTAGE`];
    const AUTOPLAY_TEST_DISABLED_PERCENTAGE = publicRuntimeConfig[`AUTOPLAY_TEST_${PLATFORM}_DISABLED_PERCENTAGE`];

    const autoplayTestGroup = getAutoplayTestGroup({
      AUTOPLAY_TEST_ANY_PERCENTAGE: parseInt(AUTOPLAY_TEST_ANY_PERCENTAGE, 10),
      AUTOPLAY_TEST_UNMUTED_PERCENTAGE: parseInt(AUTOPLAY_TEST_UNMUTED_PERCENTAGE, 10),
      AUTOPLAY_TEST_DISABLED_PERCENTAGE: parseInt(AUTOPLAY_TEST_DISABLED_PERCENTAGE, 10),
    });

    this.state = {
      fetchPlaylistData: false,
      enforceRatio: false,
      autoplayTestGroup,
    };
  }

  componentDidMount() {
    const {
      statusCode,
    } = this.props;

    if (statusCode === 200) {
      this.updatePlaylistDrawer();
      this.fetchRelatedArticle();

      BTE.monitorResize();

      BTE.on('resize', this.onResize, 'debounce');
      this.onResize({
        width: window.innerWidth || document.documentElement.clientWidth,
        height: window.innerHeight || document.documentElement.clientHeight,
      });
    }
  }

  onResize = ({ width: ww, height: wh }) => {
    const { enforceRatio } = this.state;
    const {
      top,
      width: pw,
      height: ph,
    } = (this.playerWrapper && this.playerWrapper.getBoundingClientRect()) || {};

    if (top + ph > wh) {
      if (!enforceRatio) {
        // TODO (related to BENTO-10896): figure out what race condition (probably)
        // is causing `this.drawerWrapper` to be undefined sometimes when this method
        // is called. Adding a check around `this.playerWrapper` as well, to be safe
        if (this.playerWrapper && typeof this.playerWrapper.setAttribute === 'function') {
          this.playerWrapper.setAttribute(
            'style',
            `flex-grow:unset; margin:0 auto; width:calc((100vh - ${top}px)*(16/9)); height:calc(100vh - ${top}px)`,
          );
        }
        if (this.drawerWrapper && typeof this.drawerWrapper.setAttribute === 'function') {
          this.drawerWrapper.setAttribute('style', `height:calc(100vh - ${top}px);`);
        }
        this.setState({ enforceRatio: true });
      }
    } else if ((ww < 1000 && ww === pw) || (ww >= 1000 && ww <= pw + 465)) {
      if (enforceRatio) {
        if (this.playerWrapper && typeof this.playerWrapper.removeAttribute === 'function') {
          this.playerWrapper.removeAttribute('style');
        }
        if (this.drawerWrapper && typeof this.drawerWrapper.removeAttribute === 'function') {
          this.drawerWrapper.removeAttribute('style');
        }
        this.setState({ enforceRatio: false });
      }
    }
  }

  onVideoPlay = () => {
    const { fetchPlaylistData } = this.state;
    const {
      associatedPlaylists,
      loadVideosByIds,
    } = this.props;

    if (fetchPlaylistData) {
      const ids = associatedPlaylists[0].videos.map((video) => video.mpxMetadata.id);

      // Send request for Video-teases of multiple ids.
      loadVideosByIds(ids);

      this.setState({
        fetchPlaylistData: false,
      });
    }
  }

  onVideoSelect = async (selected, playlistIndex, playlist) => {
    // Track at beginning, so it fires in both cases.
    $t('track', 'mbt_video_drawer_click', { video: selected, index: playlistIndex, playlist });

    if (!isVerticalVideo(selected) && !determineDomain(selected.url.primary).match(/(nbcnews|today|msnbc|telemundo).com/i)) {
      window.location = selected.url.primary;
      return;
    }

    let url;
    const {
      loadVideo, nowPlaying, setCurrent, vertical,
    } = this.props;

    if (!selected.id) {
      await loadVideo(selected.mpxMetadata.id, vertical);
      url = buildUrl(nowPlaying);
    } else {
      await setCurrent(selected);
      url = buildUrl(selected);
    }

    /*
     * Instead of routing, we'll update url for sharing.
     * Users most likely don't see this page as a seperate
     * pages and back button functionality will throw them off
     * so we replace state instead of push.
     */
    const parsedURL = parseOnClient(url);
    const normalizedURL = parsedURL.pathname || url;

    /**
     * We maintain the video page query parameters across
     * clicking through the playlists.
     */
    const { search: currentPageQueryParametersString } = window.location;
    const replacementURL = `${normalizedURL}${currentPageQueryParametersString}`;

    window.history.replaceState(null, null, replacementURL);
  }

  showTaboola = () => {
    const { vertical } = this.props;
    if (brandsWithTaboolaFeed.includes(vertical)) {
      return <Taboola />;
    }
    return null;
  }

  fetchRelatedArticle = () => {
    const {
      associatedPlaylists,
      loadRelatedArticle,
      loadRelatedVideo,
    } = this.props;

    const relatedContentLink = get(this.props, 'nowPlaying.relatedContentLink', { text: null, url: null });
    const relatedURL = relatedContentLink.url || associatedPlaylists[0].relatedUrl;
    if (!relatedURL) {
      return;
    }
    const relatedArticleId = parseIdFromUrl(relatedURL);
    if (!relatedArticleId) {
      return;
    }
    if (relatedArticleId.indexOf('mmvo') === 0 || relatedArticleId.indexOf('tmvo') === 0) {
      loadRelatedVideo(relatedArticleId);
    } else {
      loadRelatedArticle(relatedArticleId);
    }
  }

  updatePlaylistDrawer = () => {
    const {
      loadAssociatedPlaylist,
    } = this.props;

    // Playlist in path
    const queryParams = parseQueryString(window.location.search);
    if (queryParams && queryParams.playlist) {
      loadAssociatedPlaylist(queryParams.playlist);
    }
  }

  drawer = () => {
    const { associatedPlaylists, nowPlaying, vertical } = this.props;

    return (
      <div
        className={styles.drawer}
        ref={(element) => { this.drawerWrapper = element; }}
      >
        <PlaylistDrawer
          playlists={associatedPlaylists}
          nowPlaying={nowPlaying.id}
          layout={LAYOUT.VERTICAL}
          onSelect={this.onVideoSelect}
          vertical={vertical}
        />
      </div>
    );
  }

  bestOf = () => {
    const { vertical, topPlaylists } = this.props;
    const showBestOf = vertical.match(/news|msnbc|today|mach|think|better/i);

    if (!showBestOf || !topPlaylists || !topPlaylists[0]) {
      return null;
    }

    if (vertical.match(/today|mach|think|better/i)) {
      topPlaylists[0].headline.primary = `Best of ${vertical.toUpperCase()}`;
    }

    if (vertical === 'msnbc') {
      topPlaylists[0].headline.primary = topPlaylists[0].headline.primary.toUpperCase();
    }

    return (
      <BestOfPlaylist
        playlist={topPlaylists[0]}
        title={topPlaylists[0].headline.primary}
        vertical={vertical}
      />
    );
  }

  handleMainContentError = () => {
    const { vertical } = this.props;
    return (
      <ErrorPage
        statusCode={500}
        vertical={vertical}
      />
    );
  }

  render() {
    const {
      associatedPlaylists,
      isChromeless,
      layout,
      nowPlaying,
      relatedArticle,
      relatedVideo,
      socialMediaProfiles,
      statusCode,
      themesCssPath,
      topPlaylists,
      vertical,
    } = this.props;

    if (parseInt(statusCode, 10) >= 400) {
      return (
        <ErrorPage
          statusCode={statusCode === 404 ? 404 : 500}
          vertical={vertical}
          layout={layout}
        />
      );
    }

    const { autoplayTestGroup } = this.state;

    let relatedInfo = null;
    if (relatedVideo && relatedVideo.headline && relatedVideo.headline.primary) {
      relatedInfo = { ...relatedVideo };
    } else {
      relatedInfo = { ...relatedArticle };
    }

    const relatedMPXLinkText = get(nowPlaying, 'relatedContentLink.text', null);
    const relatedMPXLinkURL = get(nowPlaying, 'relatedContentLink.url', null);
    const originalHeadline = get(relatedInfo, 'headline', { primary: null });

    relatedInfo.description = get(relatedInfo, 'description', null);
    relatedInfo.ecommerceEnabled = get(relatedInfo, 'ecommerceEnabled', false);
    relatedInfo.headline = originalHeadline;
    relatedInfo.url = null;

    if (relatedMPXLinkText && relatedMPXLinkURL) {
      relatedInfo.headline.primary = relatedMPXLinkText;
      relatedInfo.url = relatedMPXLinkURL;
    }

    if (!relatedMPXLinkText && relatedInfo && relatedInfo.headline) {
      if (relatedInfo.headline.primary === null) {
        relatedInfo.headline = null;
      }
      relatedInfo.url = relatedMPXLinkURL;
    }

    const numberOfVideos = getFeatureConfigForBrand(MOBILE_PLAYLIST_NUMBER_OF_VIDEOS, vertical);

    const mobilePlaylist = [{
      ...associatedPlaylists[0],
      videos: (associatedPlaylists[0] && associatedPlaylists[0].videos)
        ? associatedPlaylists[0].videos.slice(0, numberOfVideos)
        : null,
    }];

    const hasPlaylister = !vertical.match(/msnbc|knowyourvalue|news|today|mach|better|think/i);

    const ignoreAutoplayTest = getFeatureConfigForBrand(IGNORE_AUTOPLAY_TEST, vertical);
    const autoplayMobileVideosMuted = getFeatureConfigForBrand(
      AUTOPLAY_CANONICAL_VIDEOS_MUTED_ON_MOBILE,
      vertical,
    );

    const isUserInAutoplayGroup = autoplayTestGroup !== AUTOPLAY_TEST_GROUPS.AUTOPLAY_DISABLED;
    const shouldCheckAutoplayCapabilities = ENABLE_AUTOPLAY_TEST && isUserInAutoplayGroup;

    const VideoPlayerWrapper = shouldCheckAutoplayCapabilities
      ? VideoAutoplayCapabilitiesDetector
      : NoDetect;

    const autoplayMuted = ignoreAutoplayTest && autoplayMobileVideosMuted && MobileDetection.any();

    return (
      <>
        <Head>
          {themesCssPath && (
            <link href={themesCssPath} rel="stylesheet" key={themesCssPath} />
          )}
          <IconfontStyleTag />
        </Head>

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          <HeaderAndFooter layout={layout}>
            <ErrorBoundary errorHandler={this.handleMainContentError}>
              <ScrollingAnalytics contentType="video" contentUrl={nowPlaying.url.primary}>
                <GlobalMetadata />
                <VideoMetadata video={nowPlaying} vertical={vertical} />

                <div className={styles.videoPage}>
                  <div className={styles.video}>
                    <div className={styles.main}>
                      <div
                        className={styles.player}
                        ref={(element) => { this.playerWrapper = element; }}
                      >
                        <VideoPlayerWrapper>
                          {({ ...autoplayCapabilities }) => (
                            <VideoPlayer
                              video={nowPlaying}
                              autoPlay
                              onlyAutoplayMuted={autoplayMuted}
                              stickyOffset={80}
                              continuousPlay
                              playlist={associatedPlaylists}
                              onAdPlay={this.onVideoPlay}
                              onVideoPlay={this.onVideoPlay}
                              onVideoEnd={this.onVideoSelect}
                              autoplayCapabilities={autoplayCapabilities}
                              autoplayTestGroup={autoplayTestGroup}
                              isTestingAutoplayCapabilities={
                                (autoplayCapabilities || {}).isTestingAutoplayCapabilities
                              }
                              vertical={vertical}
                            />
                          )}
                        </VideoPlayerWrapper>
                      </div>
                      {this.drawer()}
                    </div>

                    <VideoDetails
                      vertical={vertical}
                      video={nowPlaying}
                      social={socialMediaProfiles}
                      relatedArticle={relatedInfo}
                      isChromeless={isChromeless}
                    />
                  </div>

                  <div className={styles.carousel}>
                    <PlaylistDrawer
                      playlists={mobilePlaylist}
                      nowPlaying={nowPlaying.id}
                      layout={LAYOUT.VERTICAL}
                      onSelect={this.onVideoSelect}
                      vertical={vertical}
                    />
                  </div>

                  {hasPlaylister && <Playlister playlists={topPlaylists} />}
                  {this.bestOf()}
                  {this.showTaboola()}
                </div>
              </ScrollingAnalytics>
            </ErrorBoundary>
          </HeaderAndFooter>
        </div>

        <AnalyticsLaunchScripts />
        <AdsBundle />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps, mapActionsToProps)(
    VideoPage,
  ),
);
